// hooks/useColumnPreferences.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import SysColumn from "app/class/sys-column";
import { useAuth } from "app/providers/AuthProvider";
import queryKeys from "shared/services/queryKeys";

export interface ColumnPreference {
    label: string;
    name: string;
    visible: boolean;
    order: number;
    width?: number;
}

interface UseColumnPreferencesReturn {
    preferences: ColumnPreference[];
    isLoading: boolean;
    savePreferences: (prefs: ColumnPreference[]) => void;
}

const PREFERENCE_KEY = "record_list.columns";

function buildDefaultPreferences(columns: SysColumn[], hasChildren: boolean): ColumnPreference[] {
    return columns.map((col, index) => ({
        label: col.label,
        name: col.name,
        visible: !col.is_hidden || (hasChildren && col.name === "sys_class_name"),
        order: col.order ?? index,
    }));
}

function reconcilePreferences(
    columns: SysColumn[],
    preferences: ColumnPreference[],
    hasChildren: boolean
): ColumnPreference[] {
    if (preferences.length === 0) {
        return buildDefaultPreferences(columns, hasChildren);
    }

    const schemaNames = new Set(columns.map((column) => column.name));
    const filteredPreferences = preferences.filter((pref) => schemaNames.has(pref.name));
    const savedNames = new Set(filteredPreferences.map((pref) => pref.name));

    const newColumns = columns
        .filter((column) => !savedNames.has(column.name))
        .map((column, index) => ({
            label: column.label,
            name: column.name,
            visible: !column.is_hidden || (hasChildren && column.name === "sys_class_name"),
            order: filteredPreferences.length + index,
        }));

    return [...filteredPreferences, ...newColumns];
}

export function useColumnPreferences(
    tableName: string,
    columns: SysColumn[],
    scope = "list",
    hasChildren = false
): UseColumnPreferencesReturn {
    const queryClient = useQueryClient();
    const preferenceKey = scope === "list" ? PREFERENCE_KEY : `${PREFERENCE_KEY}.${scope}`;
    const { user } = useAuth();
    const queryKey = queryKeys.preferences.byUser(user?.id);

    const { data: preferenceRecords = [], isLoading } = useQuery({
        queryKey,
        queryFn: async () => {
            const result = await new RecordManagement("sys_user_preference")
                .where("user_id", user?.id)
                .paginate(1, 5000)
                .run();
            return result.data;
        },
        enabled: Boolean(tableName && user?.id && columns.length > 0),
    });
    const response = preferenceRecords.find((record) => record.table_name === tableName && record.preference_key === preferenceKey);
    const preferenceValue = typeof response?.preference_value === "string"
        ? JSON.parse(response.preference_value)
        : response?.preference_value;
    const saved: ColumnPreference[] = preferenceValue?.columns ?? [];
    const preferences = reconcilePreferences(columns, saved, hasChildren);

    const { mutate } = useMutation({
        mutationFn: async (prefs: ColumnPreference[]) => {

            const response = await new RecordManagement("sys_user_preference").where("table_name", tableName).where("user_id", user?.id).where("preference_key", preferenceKey).first();
            if (!response || response.id === undefined) {
                await new RecordManagement("sys_user_preference").save({
                    user_id: user?.id,
                    table_name: tableName,
                    preference_key: preferenceKey,
                    preference_value: JSON.stringify({ columns: prefs }),
                });
                return;
            }
            await new RecordManagement("sys_user_preference").update(response.id, {
                preference_value: JSON.stringify({ columns: prefs }),
            });
        },
        onSuccess: (_, prefs) => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    return {
        preferences,
        isLoading,
        savePreferences: mutate,
    };
}
